/**
 * Beatly Amplitude Analytics Module
 *
 * A generic, reusable module for Amplitude analytics integration.
 * Can be easily added to any Beatly frontend app.
 *
 * Usage:
 *   import { initAmplitude } from './utils/amplitude';
 *   initAmplitude(AMPLITUDE_CONFIG);
 */

export interface AmplitudeEnvironmentConfig {
  /** Hostname pattern to match (exact match) */
  hostname: string;
  /** Amplitude API key for this environment */
  apiKey: string;
}

export interface AmplitudeConfig {
  /** App name for logging purposes */
  appName: string;
  /** Environment configurations - matched by hostname */
  environments: AmplitudeEnvironmentConfig[];
  /** Session replay sample rate (0-1), default: 1 */
  sessionReplaySampleRate?: number;
  /** Autocapture options */
  autocapture?: {
    attribution?: boolean;
    fileDownloads?: boolean;
    formInteractions?: boolean;
    pageViews?: boolean;
    sessions?: boolean;
    elementInteractions?: boolean;
    networkTracking?: boolean;
    webVitals?: boolean;
    frustrationInteractions?: boolean;
  };
}

/** Default autocapture configuration */
const DEFAULT_AUTOCAPTURE = {
  attribution: true,
  fileDownloads: true,
  formInteractions: true,
  pageViews: true,
  sessions: true,
  elementInteractions: true,
  networkTracking: true,
  webVitals: true,
  frustrationInteractions: true,
};

/** Track whether Amplitude has been initialized */
let isInitialized = false;
let isEnabled = false;

/**
 * Get the API key for the current environment based on hostname
 */
function getApiKeyForEnvironment(environments: AmplitudeEnvironmentConfig[]): string | null {
  const hostname = window.location.hostname;

  for (const env of environments) {
    if (hostname === env.hostname) {
      return env.apiKey;
    }
  }

  return null;
}

/**
 * Dynamically load the Amplitude SDK script
 */
function loadAmplitudeScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Safety check for SSR
    if (typeof window === 'undefined') {
      reject(new Error('Cannot load Amplitude SDK on server'));
      return;
    }

    // Check if script is already loaded
    if (window.amplitude) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://cdn.eu.amplitude.com/script/${apiKey}.js`;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Amplitude SDK'));

    document.head.appendChild(script);
  });
}

/**
 * Initialize Amplitude with environment-based configuration
 *
 * @param config - Amplitude configuration with environment mappings
 * @returns Promise that resolves when initialization is complete
 */
export async function initAmplitude(config: AmplitudeConfig): Promise<boolean> {
  // Safety check for SSR
  if (typeof window === 'undefined') {
    return false;
  }

  if (isInitialized) {
    console.warn(`[${config.appName}] Amplitude already initialized`);
    return isEnabled;
  }

  const apiKey = getApiKeyForEnvironment(config.environments);

  if (!apiKey) {
    console.info(
      `[${config.appName}] Amplitude disabled for this environment (${window.location.hostname})`
    );
    isInitialized = true;
    isEnabled = false;
    return false;
  }

  try {
    await loadAmplitudeScript(apiKey);

    // Add session replay plugin if available
    // Note: sessionReplay might not be available immediately after script load
    if (window.sessionReplay && typeof window.sessionReplay.plugin === 'function') {
      try {
        const sampleRate = config.sessionReplaySampleRate ?? 1;
        window.amplitude.add(window.sessionReplay.plugin({ sampleRate }));
      } catch (pluginError) {
        console.warn(`[${config.appName}] Failed to add session replay plugin:`, pluginError);
        // Continue initialization without session replay
      }
    } else {
      console.warn(`[${config.appName}] Session replay not available, skipping plugin`);
    }

    // Initialize with configuration
    window.amplitude.init(apiKey, {
      fetchRemoteConfig: true,
      serverZone: 'EU',
      autocapture: config.autocapture ?? DEFAULT_AUTOCAPTURE,
    });

    console.info(`[${config.appName}] Amplitude initialized for ${window.location.hostname}`);
    isInitialized = true;
    isEnabled = true;
    return true;
  } catch (error) {
    console.error(`[${config.appName}] Failed to initialize Amplitude:`, error);
    isInitialized = true;
    isEnabled = false;
    return false;
  }
}

/**
 * Check if Amplitude is enabled and ready
 */
export function isAmplitudeEnabled(): boolean {
  return isEnabled;
}

/**
 * Track a custom event with optional properties
 * Safe to call even if Amplitude is disabled
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (isEnabled && window.amplitude) {
    window.amplitude.track(eventName, properties);
  }
}

/**
 * Identify a user by their ID
 * Safe to call even if Amplitude is disabled
 */
export function identifyUser(userId: string): void {
  if (isEnabled && window.amplitude) {
    window.amplitude.identify(userId);
  }
}

/**
 * Set or clear the user ID
 * Safe to call even if Amplitude is disabled
 */
export function setUserId(userId: string | null): void {
  if (isEnabled && window.amplitude) {
    window.amplitude.setUserId(userId);
  }
}

/**
 * Reset the Amplitude instance (clears user data)
 * Safe to call even if Amplitude is disabled
 */
export function resetAmplitude(): void {
  if (isEnabled && window.amplitude) {
    window.amplitude.reset();
  }
}

/**
 * Opt user out of analytics tracking
 * Safe to call even if Amplitude is disabled
 */
export function setOptOut(optOut: boolean): void {
  if (isEnabled && window.amplitude) {
    window.amplitude.setOptOut(optOut);
  }
}

// ============================================================================
// Beatly Planner App Configuration
// ============================================================================

/**
 * Amplitude configuration for Beatly Planner App
 * Import and use this with initAmplitude() in main.tsx
 */
export const PLANNER_APP_AMPLITUDE_CONFIG: AmplitudeConfig = {
  appName: 'Beatly Planner',
  environments: [
    {
      hostname: 'planner.beatly.com',
      apiKey: '85d8b025260b318a774894f00d517d85',
    },
    {
      hostname: 'planner.staging.beatly.com',
      apiKey: '9932ac102ee548920fffe1b0f6f8e0eb',
    },
  ],
  sessionReplaySampleRate: 1,
};
